import { Ref, forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Box, Button, Stack, useTheme } from "@mui/material";
import { ScheduledReportsNotificationTabEnum, UseScheduledReportsNotificationStateType } from "./ScheduledReportsNotificationsState";
import { useFieldArray, useFormContext } from "react-hook-form";
import { ScheduledReportsFormType } from "../ScheduledReportsEditorUtils";
import { IOrganization } from "../../../services/organization-data-service/Models";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { useTranslation } from "react-i18next";
import { isMobile, isTablet } from "react-device-detect";
import ScheduledReportsRecipientList, { ScheduledReportsRecipientListType } from "./scheduled-reports-recipients/ScheduledReportsRecipientList";
import { RecipientActions, recipientActionItems } from "./ScheduledReportsNotificationUtils";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useOrganizations } from "../../../queries/organizations/UseOrganizations";
import { BaseActions, ActionMenuItem } from "../../action-menu/ActionMenuItem";
import { ViewOptionStateEnum } from "../../contextual-app-bar/ViewOptionStateEnum";
import FormNotificationLists, { FormNotificationListsType } from "../../form-notification-lists/FormNotificationLists";
import NoData from "../../no-data/NoData";
import OrganizationPicker from "../../organization-picker/OrganizationPicker";
import SideSheetFilterInput from "../../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetSwitch from "../../side-sheet/side-sheet-switch/SideSheetSwitch";
import SideSheet from "../../side-sheet/side-sheet/SideSheet";
import TabBar from "../../tab-bar/TabBar";
import TabContainer from "../../tab-container/TabContainer";
import TabWithCount from "../../tab-with-count/TabWithCount";
import { FormNotificationListsActions, notificationListActionItems } from "../../form-notification-lists/FormNotificationListsUtils";
import { RecipientTypeEnum } from "../../../common/enums/RecipientTypeEnum";

export type ScheduledReportsNotificationsType = {
    executeAction: (action: RecipientActions | BaseActions) => void;
};


export type ScheduledReportsNotificationsProps = {
    organizationID: number,
    groupID: number,
    openSideSheet?: boolean;
    onCloseSideSheet?: () => void;
    setViewOptionState?: (value: ViewOptionStateEnum) => void;
    notificationState: UseScheduledReportsNotificationStateType
};

function ScheduledReportsNotifications({
    organizationID,
    groupID,
    openSideSheet,
    onCloseSideSheet,
    setViewOptionState,
    notificationState
    
}: ScheduledReportsNotificationsProps, ref: Ref<unknown> | undefined) {

    const isPhone = isMobile && !isTablet;
    const theme = useTheme();
    const { t } = useTranslation();
    const recipientList = useRef<ScheduledReportsRecipientListType>(undefined);
    const notificationList = useRef<FormNotificationListsType>(undefined);

    const {data: organizations} = useQueryNotifications(useOrganizations)({
    });

    const organization = organizations?.find(o => o.id === organizationID);


    useImperativeHandle<unknown, ScheduledReportsNotificationsType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );


    const {
        state, 
        setActiveTab,
        setViewSelected,
        setRecipientFilterText,
        setNotificationListFilterText,
        //setAlarmSchemeFilterText,
        setOrganization,
        resetState

    //} = useScheduledReportsNotificationState();
    } = notificationState;

    const {
        //getValues,
        watch,
        //trigger,
        control,
        //reset,
        //setValue,
        //resetField
    } = useFormContext<ScheduledReportsFormType>();    

    const {
        fields: recipients,
        //append,
        //remove,
        //update
    } = useFieldArray({
        control: control,
        name: 'recipients',
        keyName: "fieldKey"
    });    

    const {
        fields: notificationLists,
        //append,
        //remove,
        //update
    } = useFieldArray({
        control: control,
        name: 'notificationLists',
        keyName: "fieldKey"
    });    

    const [
        watchRecipients,
        watchNotificationLists
    ] = watch([
        'recipients',
        'notificationLists'
    ]);


     const objectType = state.activeTab === ScheduledReportsNotificationTabEnum.Recipients ?
                        eLynxObjectTypeEnum.Recipient :
                        eLynxObjectTypeEnum.NotificationList;

    const recipientCount = recipients.length;
    const notificationListCount = notificationLists.length;

    const count = recipientCount + notificationListCount;
    const selectedRecipientCount = watchRecipients.filter(o => o.selected).length;
    const selectedNotificationListCount = watchNotificationLists.filter(o => o.selected).length;

    const enableViewSelected = state.activeTab === ScheduledReportsNotificationTabEnum.Recipients ? 
                selectedRecipientCount > 0 :
                selectedNotificationListCount > 0;


    function executeAction(action: RecipientActions | BaseActions) {

        if (state.activeTab === ScheduledReportsNotificationTabEnum.Recipients) {
            recipientList.current?.executeAction(action);
        }

    }                        
    function onExecuteAction(menuItem: ActionMenuItem) {

        switch(state.activeTab) {
            case ScheduledReportsNotificationTabEnum.Recipients: {
                    const action = menuItem.action as RecipientActions;
                    if (action === RecipientActions.Add) {
                        recipientList.current?.executeAction(action);
                    }
                }

                break;

            case ScheduledReportsNotificationTabEnum.NotificationLists: {
                    const action = menuItem.action as FormNotificationListsActions | BaseActions;
                    notificationList.current?.executeAction(action);
                }
                break;
        }
    }

    function onViewSelected(value: boolean) {
        setViewSelected(value);
    }

    function onSetFilter(value: string) {

        objectType === eLynxObjectTypeEnum.Recipient ?
            setRecipientFilterText(value) :
            setNotificationListFilterText(value);

    }

    function onOrganizationChanged(item: IOrganization) {
        setOrganization(item.id);
    }

    useEffect(() => {
        setViewOptionState && setViewOptionState(state.viewOptionState);
    },[
        state.viewOptionState,
        setViewOptionState
    ]);

    useEffect(() => {
        if (state.viewSelected) {
                
            if (state.activeTab === ScheduledReportsNotificationTabEnum.NotificationLists && selectedNotificationListCount === 0) {
                setViewSelected(false);

            } else if (state.activeTab === ScheduledReportsNotificationTabEnum.Recipients && selectedRecipientCount === 0) {
                setViewSelected(false);
            } 

        }
    }, [
        state.viewSelected,
        state.activeTab,
        setViewSelected,
        selectedRecipientCount,
        selectedNotificationListCount,
    ]);

   return ((count > 0) ? <Box
       sx={{
           display: 'flex',
           flexDirection: 'column',
           flex: '1 1 auto',
           overflow: 'hidden',
           position: 'relative'
       }}>
       <TabBar value={state.activeTab}
           onChange={(_event, value) => setActiveTab(value)}>


           <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.Recipient, true)} 
               count={selectedRecipientCount}
               value={ScheduledReportsNotificationTabEnum.Recipients}
           />

           
           <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.NotificationList, true)} 
               count={selectedNotificationListCount}
               value={ScheduledReportsNotificationTabEnum.NotificationLists}
           /> 

       </TabBar>

        {!isPhone ? 
            // NOTE this has to position absolute, othwerwise the Tab component throws errors
            <Box
                sx={{
                    position: 'absolute',
                    top: theme.spacing(1),
                    right: theme.spacing(3)
                }}>

                {(state.activeTab === ScheduledReportsNotificationTabEnum.Recipients ? 
                                recipientActionItems : notificationListActionItems).map((o, ndx) => 
                                    <Button key={ndx} onClick={() => onExecuteAction(o)}>
                                        {o.label}
                                    </Button>
                                )
                }

            </Box> : 
            null 
        }

       <Stack
           direction='row'
           sx={{
               display: 'flex',
               flex: '1 1 auto',
               overflow: 'hidden'
           }}>        

           <TabContainer visible={state.activeTab === ScheduledReportsNotificationTabEnum.Recipients}>
               <ScheduledReportsRecipientList 
                   ref={recipientList}
                   visible={state.activeTab === ScheduledReportsNotificationTabEnum.Recipients}
                   filterText={state.recipientFilterText}
                   viewSelected={state.viewSelected}
                   organizationID={state.organizationID === -1 ? undefined : state.organizationID} 

               />
           </TabContainer>


           <TabContainer visible={state.activeTab === ScheduledReportsNotificationTabEnum.NotificationLists}>
               <FormNotificationLists visible={state.activeTab === ScheduledReportsNotificationTabEnum.NotificationLists}
                       ref={notificationList}
                       groupID={groupID}
                       filterText={state.notificationListFilterText}
                       viewSelected={state.viewSelected}
                       organizationID={state.organizationID === -1 ? undefined : state.organizationID} 
                       allowEdit={false}
                       recipientType={RecipientTypeEnum.Email}
               />
           </TabContainer>


           <SideSheet
               title={t("options")}
               open={openSideSheet ?? false}
               hideCloseButton={!isPhone}
               onClose={() => onCloseSideSheet && onCloseSideSheet()}
               onReset={resetState}>


               <OrganizationPicker 
                   label={getObjectTypeName(eLynxObjectTypeEnum.Organization)}
                   color="inherit"
                   organizationID={state.organizationID ?? -1 } 
                   onSelected={onOrganizationChanged}
                   includeAllOption={true}
               />

               <SideSheetSwitch
                   label={t("setpointAdmin.viewSelected", {name: getObjectTypeName(objectType, true)})}
                   checked={state.viewSelected}
                   onChange={(checked: boolean) => {
                       onViewSelected(checked);
                   }}
                   disabled={!enableViewSelected} />

               <SideSheetFilterInput
                   label={t("setpointAdmin.filterByObject", {objectType: getObjectTypeName(objectType)})}
                   value={state.activeTab === ScheduledReportsNotificationTabEnum.Recipients ?  state.recipientFilterText :
                       state.notificationListFilterText}
                   onChange={(value) => {
                       onSetFilter(value);
                   }} 
                   disabled={state.viewSelected}/>


              

           </SideSheet>

       </Stack>

   </Box> : <Box sx={{
       padding: 1
   }}>
       <NoData text={t('scheduledReports.nodata.textall', {org: organization?.displayText })} />
   </Box>);
  }


  export default forwardRef(ScheduledReportsNotifications);